import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Slider,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import GridOnIcon from '@mui/icons-material/GridOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import {
  ScatterChart as RechartsScatterChart,
  Scatter as RechartsScatter,
  XAxis as RechartsXAxis,
  YAxis as RechartsYAxis,
  CartesianGrid as RechartsCartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine as RechartsReferenceLine,
  Cell as RechartsCell,
  LabelList as RechartsLabelList,
} from 'recharts';

import { VerticalBarChart, LineChart } from '../../../../../shared/components/charts';
import {
  DashboardCard,
  DashboardCardSkeleton,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
} from '../../../../../shared/components';
import {
  useMetricsComparison,
  useMetricsTrends,
  useMetricsStats,
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
} from '../../../../../shared/hooks';
import { buildFilterParams, transformTrendsData, calculateGranularity, linearFit } from '../../shared/utils';
import { toComparisonKey } from '../../../../../shared/utils';
import { useClientScope } from '../../../../../shared/contexts';
import { brandColors } from '../../../../../theme/brandColors';

// Categorical palette for per-segment scatter points (matches brand chart palette).
const SCATTER_PALETTE = [
  brandColors.peak4Blue,
  brandColors.summitGreen,
  brandColors.performOrange,
  brandColors.fuelIndigo,
  brandColors.recoverPink,
  brandColors.focusLilac,
];

// ─── Metric catalogue ────────────────────────────────────────────────────────

const METRIC_OPTIONS: Array<{ label: string; value: string; unit: string; group: string }> = [
  // Recovery
  { label: 'Recovery Score', value: 'recovery_score', unit: '%', group: 'Recovery' },
  { label: 'Recovery', value: 'recovery', unit: '%', group: 'Recovery' },
  { label: 'HRV', value: 'hrv', unit: 'ms', group: 'Recovery' },
  { label: 'Average HRV', value: 'average_hrv', unit: 'ms', group: 'Recovery' },
  { label: 'Stress Score', value: 'stress_score', unit: '', group: 'Recovery' },
  { label: 'Stress Recovery Balance', value: 'stress_recovery_balance', unit: '', group: 'Recovery' },
  { label: 'High Stress Days', value: 'high_stress_days', unit: 'days', group: 'Recovery' },
  // Sleep
  { label: 'Sleep', value: 'sleep', unit: '%', group: 'Sleep' },
  { label: 'Sleep Performance', value: 'sleep_performance', unit: '%', group: 'Sleep' },
  { label: 'Sleep Efficiency', value: 'sleep_efficiency', unit: '%', group: 'Sleep' },
  { label: 'Sleep Consistency', value: 'sleep_consistency', unit: '%', group: 'Sleep' },
  { label: 'Sleep Stress', value: 'sleep_stress', unit: '%', group: 'Sleep' },
  { label: 'Sleep Debt', value: 'sleep_debt', unit: 'hrs', group: 'Sleep' },
  { label: 'Hours of Sleep', value: 'hours_of_sleep', unit: 'hrs', group: 'Sleep' },
  { label: 'Hours of Sleep Needed', value: 'hours_of_sleep_needed', unit: 'hrs', group: 'Sleep' },
  { label: 'Nap Duration', value: 'nap_duration', unit: 'hrs', group: 'Sleep' },
  { label: 'Awake Stage', value: 'sleep_stage_awake', unit: 'hr', group: 'Sleep' },
  { label: 'Light Sleep Stage', value: 'sleep_stage_light', unit: 'hr', group: 'Sleep' },
  { label: 'REM Sleep Stage', value: 'sleep_stage_rem', unit: 'hr', group: 'Sleep' },
  { label: 'Deep Sleep Stage', value: 'sleep_stage_deep', unit: 'hr', group: 'Sleep' },
  // Strain & Activity
  { label: 'Strain', value: 'strain', unit: '', group: 'Strain & Activity' },
  { label: 'Day Strain', value: 'day_strain', unit: '', group: 'Strain & Activity' },
  { label: 'Active Time', value: 'active_time', unit: 'min', group: 'Strain & Activity' },
  { label: 'Calories Burned', value: 'calories_burn', unit: 'kcal', group: 'Strain & Activity' },
  { label: 'Cardio Load', value: 'cardio_load', unit: 'kJ', group: 'Strain & Activity' },
  { label: 'Steps', value: 'steps', unit: '', group: 'Strain & Activity' },
  // Heart Rate & Vitals
  { label: 'Resting Heart Rate', value: 'resting_heart_rate', unit: 'bpm', group: 'Heart Rate & Vitals' },
  { label: 'Heart Rate', value: 'heart_rate', unit: 'bpm', group: 'Heart Rate & Vitals' },
  { label: 'Avg Heart Rate', value: 'avg_heart_rate', unit: 'bpm', group: 'Heart Rate & Vitals' },
  { label: 'Max Heart Rate', value: 'max_heart_rate', unit: 'bpm', group: 'Heart Rate & Vitals' },
  { label: 'Respiratory Rate', value: 'respiratory_rate', unit: 'rpm', group: 'Heart Rate & Vitals' },
  { label: 'SpO2', value: 'spo2', unit: '%', group: 'Heart Rate & Vitals' },
  { label: 'Temperature', value: 'temperature', unit: '°C', group: 'Heart Rate & Vitals' },
  // Programme
  { label: 'Participation Rate', value: 'participation_rate', unit: '%', group: 'Programme' },
  { label: 'Completion Rate', value: 'completion_rate', unit: '%', group: 'Programme' },
  { label: 'High Risk Employees', value: 'high_risk_employees', unit: '%', group: 'Programme' },
  { label: 'Medium Risk Employees', value: 'medium_risk_employees', unit: '%', group: 'Programme' },
  { label: 'Low Risk Employees', value: 'low_risk_employees', unit: '%', group: 'Programme' },
  { label: 'Pace of Aging', value: 'pace_of_aging', unit: '', group: 'Programme' },
];

const metricLabel = (key: string) => METRIC_OPTIONS.find((m) => m.value === key)?.label ?? key;

const metricUnit = (key: string) => METRIC_OPTIONS.find((m) => m.value === key)?.unit ?? '';

// ─── Segmentation options ─────────────────────────────────────────────────────

type GroupByApiValue = 'department' | 'age_group' | 'gender' | 'shift_pattern' | 'user';

const SEGMENT_OPTIONS: Array<{ label: string; value: GroupByApiValue }> = [
  { label: 'Department / Role', value: 'department' },
  { label: 'Age Group', value: 'age_group' },
  { label: 'Gender', value: 'gender' },
  { label: 'Shift Pattern', value: 'shift_pattern' },
  { label: 'Individual Users', value: 'user' },
];

// ─── Chart view tabs ─────────────────────────────────────────────────────────

type ViewTab = 'trend' | 'correlation' | 'segmented' | 'matrix';

// Default metric set for the correlation matrix.
const DEFAULT_MATRIX_METRICS = ['sleep_efficiency', 'recovery_score', 'hrv', 'resting_heart_rate'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Pearson correlation coefficient between two equal-length numeric arrays.
 * Returns null when there are fewer than 2 valid pairs.
 */
function pearson(xs: number[], ys: number[]): number | null {
  const pairs = xs.map((x, i) => [x, ys[i]] as [number, number]).filter(([x, y]) => isFinite(x) && isFinite(y));
  if (pairs.length < 2) return null;
  const n = pairs.length;
  const meanX = pairs.reduce((s, [x]) => s + x, 0) / n;
  const meanY = pairs.reduce((s, [, y]) => s + y, 0) / n;
  const num = pairs.reduce((s, [x, y]) => s + (x - meanX) * (y - meanY), 0);
  const denX = Math.sqrt(pairs.reduce((s, [x]) => s + (x - meanX) ** 2, 0));
  const denY = Math.sqrt(pairs.reduce((s, [, y]) => s + (y - meanY) ** 2, 0));
  if (denX === 0 || denY === 0) return null;
  return num / (denX * denY);
}

/** Format a percentage change string for display. */
function formatChange(change: string | undefined): {
  text: string;
  positive: boolean | null;
} {
  if (!change) return { text: '', positive: null };
  const trimmed = change.trim();
  const positive = trimmed.startsWith('+');
  const negative = trimmed.startsWith('-');
  return {
    text: trimmed,
    positive: positive ? true : negative ? false : null,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SummaryStatCardProps {
  label: string;
  value: number | null | undefined;
  unit: string;
  change?: string;
  color: string;
}

const SummaryStatCard: React.FC<SummaryStatCardProps> = ({ label, value, unit, change, color }) => {
  const theme = useTheme();
  const { text, positive } = formatChange(change);
  const trendColor =
    positive === true
      ? theme.palette.success.main
      : positive === false
        ? theme.palette.error.main
        : theme.palette.text.secondary;
  const TrendIcon = positive === true ? TrendingUpIcon : positive === false ? TrendingDownIcon : TrendingFlatIcon;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant='caption' color='text.secondary'>
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography variant='h5' fontWeight={700}>
          {value != null ? Math.round(value * 10) / 10 : '—'}
        </Typography>
        {unit && (
          <Typography variant='caption' color='text.secondary'>
            {unit}
          </Typography>
        )}
      </Box>
      {text && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: trendColor }}>
          <Typography variant='caption' fontWeight={600} sx={{ color: 'inherit' }}>
            {text}
          </Typography>
          <TrendIcon sx={{ fontSize: '0.75rem' }} />
          <Typography variant='caption' color='text.secondary'>
            vs prev. period
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const MeasureComparisonInsights: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised, selectedClientAnonymised } = useClientScope();

  // Hide "Individual Users" when the selected client has anonymisation enabled.
  const availableSegmentOptions = selectedClientAnonymised
    ? SEGMENT_OPTIONS.filter((s) => s.value !== 'user')
    : SEGMENT_OPTIONS;

  // ── Persistent state ─────────────────────────────────────────────────────
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { filters, setFilters } = usePersistedFilters({
    storageKey: 'peak4.admin.measure-comparison.filters.v1',
  });

  // ── Local UI state ────────────────────────────────────────────────────────
  const [metricA, setMetricA] = useState<string>('sleep_efficiency');
  const [metricB, setMetricB] = useState<string>('recovery_score');
  const [segmentBy, setSegmentBy] = useState<GroupByApiValue>('department');
  const [selectedSegmentValues, setSelectedSegmentValues] = useState<string[]>([]);
  const [activeView, setActiveView] = useState<ViewTab>('trend');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Metric A (x-axis) range slider for the correlation scatter. null = full range.
  const [xRange, setXRange] = useState<[number, number] | null>(null);

  // Metrics plotted in the correlation matrix (heatmap of pairwise Pearson r).
  const [matrixMetrics, setMatrixMetrics] = useState<string[]>(DEFAULT_MATRIX_METRICS);

  // Per-point text labels on the scatter — off by default to avoid overlap clutter.
  const [showScatterLabels, setShowScatterLabels] = useState(false);

  // Reset segmentBy if the current selection is no longer available (e.g. anonymisation toggled on).
  useEffect(() => {
    if (segmentBy === 'user' && selectedClientAnonymised) {
      setSegmentBy('department');
      setSelectedSegmentValues([]);
    }
  }, [segmentBy, selectedClientAnonymised]);

  const activeFiltersCount = getAppliedFilterCount(filters);

  // ── Derived params ────────────────────────────────────────────────────────
  const filterParams = useMemo(
    () => ({
      ...buildFilterParams(filters, dateRange, effectiveClientId),
      bypass_anonymisation: isCentralAdmin,
    }),
    [filters, dateRange, effectiveClientId, isCentralAdmin],
  );

  const granularity = useMemo(() => calculateGranularity(dateRange), [dateRange]);

  const metricsForQuery = useMemo(() => [metricA, metricB], [metricA, metricB]);

  // Comparison API uses different keys than the UI for some metrics
  const comparisonKeyA = useMemo(() => toComparisonKey(metricA), [metricA]);
  const comparisonKeyB = useMemo(() => toComparisonKey(metricB), [metricB]);
  const comparisonMetrics = useMemo(() => {
    const keys = [comparisonKeyA, comparisonKeyB].filter(Boolean) as string[];
    return Array.from(new Set(keys));
  }, [comparisonKeyA, comparisonKeyB]);

  // Clear selected segment values whenever the dimension changes
  const handleSegmentByChange = (val: GroupByApiValue) => {
    setSegmentBy(val);
    setSelectedSegmentValues([]);
  };

  // ── Data queries ──────────────────────────────────────────────────────────
  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const { data: statsData, isLoading: statsLoading } = useMetricsStats({
    ...filterParams,
    metrics: metricsForQuery,
  });

  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useMetricsTrends({
    ...filterParams,
    metrics: metricsForQuery,
    granularity,
  });

  // Segmented comparison — used for both bar chart and scatter plot.
  // Uses comparison-API-normalised keys (e.g. hours_of_sleep → sleep_duration).
  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    error: comparisonError,
  } = useMetricsComparison({
    ...filterParams,
    group_by: segmentBy,
    metrics: comparisonMetrics.length ? comparisonMetrics : metricsForQuery,
  });

  // Correlation matrix comparison — one query for all selected matrix metrics,
  // grouped by the same segment dimension. Pearson r computed client-side per pair.
  const matrixComparisonMetrics = useMemo(() => {
    const keys = matrixMetrics.map((m) => toComparisonKey(m)).filter(Boolean) as string[];
    return Array.from(new Set(keys));
  }, [matrixMetrics]);

  const {
    data: matrixData,
    isLoading: matrixLoading,
    error: matrixError,
  } = useMetricsComparison({
    ...filterParams,
    group_by: segmentBy,
    metrics: matrixComparisonMetrics.length ? matrixComparisonMetrics : matrixMetrics,
  });

  // ── Derived data ──────────────────────────────────────────────────────────

  const metricARange = useMemo(() => {
    const minMap = overallRangeQuery.data?.data?.metric_min ?? {};
    const maxMap = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      min: typeof minMap[metricA] === 'number' ? (minMap[metricA] as number) : undefined,
      max: typeof maxMap[metricA] === 'number' ? (maxMap[metricA] as number) : undefined,
    };
  }, [overallRangeQuery.data, metricA]);

  const statA = statsData?.data?.[metricA];
  const statB = statsData?.data?.[metricB];

  const trendChartData = useMemo(() => {
    if (!trendsData) return [];
    return transformTrendsData(trendsData, [metricA, metricB]);
  }, [trendsData, metricA, metricB]);

  // All available group labels from the loaded comparison data
  const availableSegmentValues = useMemo(() => {
    if (!comparisonData?.data?.groups) return [];
    return comparisonData.data.groups.map((g) => g.group_label).filter(Boolean);
  }, [comparisonData]);

  // Groups after applying the segment value filter (empty selection = show all)
  // Also filter out segments with no data for either selected metric (privacy: insufficient data)
  // Use comparison API keys when reading values from comparison response
  const ckA = comparisonKeyA ?? metricA;
  const ckB = comparisonKeyB ?? metricB;

  const filteredGroups = useMemo(() => {
    if (!comparisonData?.data?.groups) return [];
    let groups = comparisonData.data.groups;
    if (selectedSegmentValues.length > 0) {
      groups = groups.filter((g) => selectedSegmentValues.includes(g.group_label));
    }
    // Filter out segments where both metrics lack data (insufficient data points)
    return groups.filter((g) => g.values[ckA] != null || g.values[ckB] != null);
  }, [comparisonData, selectedSegmentValues, ckA, ckB]);

  // Track how many segments were hidden due to insufficient data
  const hiddenSegmentCount = useMemo(() => {
    if (!comparisonData?.data?.groups) return 0;
    let groups = comparisonData.data.groups;
    if (selectedSegmentValues.length > 0) {
      groups = groups.filter((g) => selectedSegmentValues.includes(g.group_label));
    }
    return groups.filter((g) => g.values[ckA] == null && g.values[ckB] == null).length;
  }, [comparisonData, selectedSegmentValues, ckA, ckB]);

  const barChartData = useMemo(() => {
    if (!filteredGroups.length) return [];
    const segLabel = availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    return filteredGroups.map((group) => ({
      [segLabel]: group.group_label,
      [metricLabel(metricA)]: group.values[ckA] ?? 0,
      [metricLabel(metricB)]: group.values[ckB] ?? 0,
    }));
  }, [filteredGroups, segmentBy, metricA, metricB, ckA, ckB]);

  /**
   * Scatter data: each group becomes one point.
   * x = Metric A average for the group, y = Metric B average.
   * When grouped by "user", each point is a single person.
   */
  const scatterData = useMemo(() => {
    return filteredGroups
      .filter((g) => g.values[ckA] != null && g.values[ckB] != null)
      .map((g, i) => ({
        id: i,
        x: g.values[ckA] as number,
        y: g.values[ckB] as number,
        label: g.group_label,
        color: SCATTER_PALETTE[i % SCATTER_PALETTE.length],
      }));
  }, [filteredGroups, ckA, ckB]);

  /** Full Metric-A value extent across all plotted points (drives the slider bounds). */
  const xDomain = useMemo<[number, number] | null>(() => {
    if (scatterData.length === 0) return null;
    const xs = scatterData.map((p) => p.x);
    const min = Math.floor(Math.min(...xs));
    const max = Math.ceil(Math.max(...xs));
    return min === max ? [min, max + 1] : [min, max];
  }, [scatterData]);

  // Reset the slider selection whenever the underlying domain changes
  // (metric / segment / filter change), so it always spans the full range.
  useEffect(() => {
    setXRange(null);
  }, [xDomain?.[0], xDomain?.[1]]);

  const effectiveXRange = xRange ?? xDomain;

  /** Points within the current x-range slider selection. */
  const visibleScatterData = useMemo(() => {
    if (!effectiveXRange) return scatterData;
    const [lo, hi] = effectiveXRange;
    return scatterData.filter((p) => p.x >= lo && p.x <= hi);
  }, [scatterData, effectiveXRange]);

  /** Pearson correlation across visible scatter points. */
  const correlation = useMemo(() => {
    if (visibleScatterData.length < 2) return null;
    return pearson(
      visibleScatterData.map((p) => p.x),
      visibleScatterData.map((p) => p.y),
    );
  }, [visibleScatterData]);

  /**
   * Linear regression slope/intercept for annotation display.
   */
  const regression = useMemo(() => linearFit(visibleScatterData), [visibleScatterData]);

  /**
   * Correlation matrix: pairwise Pearson r between every selected matrix metric,
   * computed across the segment groups returned by the comparison query.
   * cells[i][j] = { r, n } where n is the number of groups with both values.
   */
  const matrix = useMemo(() => {
    const groups = matrixData?.data?.groups ?? [];
    // Build a per-metric vector of group values (using comparison-API keys).
    const vectors = matrixMetrics.map((m) => {
      const key = toComparisonKey(m) ?? m;
      return groups.map((g) => g.values[key]);
    });

    const cells = matrixMetrics.map((_rowM, i) =>
      matrixMetrics.map((_colM, j) => {
        const xs: number[] = [];
        const ys: number[] = [];
        for (let k = 0; k < groups.length; k++) {
          const xv = vectors[i][k];
          const yv = vectors[j][k];
          if (xv != null && yv != null && isFinite(xv) && isFinite(yv)) {
            xs.push(xv);
            ys.push(yv);
          }
        }
        const r = i === j ? 1 : pearson(xs, ys);
        return { r, n: xs.length };
      }),
    );
    return { cells, groupCount: groups.length };
  }, [matrixData, matrixMetrics]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newValue: ViewTab | null) => {
    if (newValue !== null) setActiveView(newValue);
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderTrendView = (height = 420) => {
    if (trendsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
          <CircularProgress />
        </Box>
      );
    }
    if (trendsError) {
      return <Alert severity='error'>Failed to load trend data. Please try again.</Alert>;
    }
    if (trendChartData.length === 0) {
      return <Alert severity='info'>No trend data available for the selected period.</Alert>;
    }

    return (
      <LineChart
        data={trendChartData}
        xAxisKey='label'
        series={[
          {
            dataKey: metricA,
            label: metricLabel(metricA),
            color: theme.palette.info.main,
            area: true,
          },
          {
            dataKey: metricB,
            label: metricLabel(metricB),
            color: theme.palette.success.main,
            area: true,
          },
        ]}
        height={height}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={metricARange.min}
        referenceMax={metricARange.max}
      />
    );
  };

  const renderScatterView = (height = 420) => {
    if (comparisonLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
          <CircularProgress />
        </Box>
      );
    }
    if (comparisonError) {
      return <Alert severity='error'>Failed to load comparison data. Please try again.</Alert>;
    }
    if (!comparisonKeyA || !comparisonKeyB) {
      const unsupported = [!comparisonKeyA ? metricLabel(metricA) : null, !comparisonKeyB ? metricLabel(metricB) : null]
        .filter(Boolean)
        .join(' and ');
      return (
        <Alert severity='warning'>
          <strong>{unsupported}</strong> {!comparisonKeyA && !comparisonKeyB ? 'are' : 'is'} not available for
          correlation comparison. Try selecting metrics from: Recovery, HRV, Sleep, Strain, Heart Rate, or Respiratory
          Rate.
        </Alert>
      );
    }
    if (scatterData.length === 0) {
      return (
        <Alert severity='info'>
          No data points to plot. Try expanding the date range or changing the segment dimension.
        </Alert>
      );
    }

    const segLabel = availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;

    const regressionPoints =
      regression && visibleScatterData.length >= 2
        ? (() => {
            const xs = visibleScatterData.map((p) => p.x);
            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            return [
              { x: minX, ry: regression.slope * minX + regression.intercept },
              { x: maxX, ry: regression.slope * maxX + regression.intercept },
            ];
          })()
        : null;

    // Snap the chart's x-domain to the slider selection so the visible plot zooms.
    const chartXDomain: [number, number] | undefined = effectiveXRange ?? undefined;

    const sliderStep = xDomain ? Math.max(0.1, Math.round(((xDomain[1] - xDomain[0]) / 100) * 10) / 10) : 1;

    return (
      <Box>
        {hiddenSegmentCount > 0 && (
          <Alert severity='warning' sx={{ mb: 1.5 }}>
            {hiddenSegmentCount} segment{hiddenSegmentCount !== 1 ? 's were' : ' was'} hidden due to insufficient data.
          </Alert>
        )}
        {correlation !== null && (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant='caption' color='text.secondary'>
              Pearson r:
            </Typography>
            <Chip
              label={correlation.toFixed(3)}
              size='small'
              color={Math.abs(correlation) >= 0.7 ? 'success' : Math.abs(correlation) >= 0.4 ? 'warning' : 'default'}
            />
            <Typography variant='caption' color='text.secondary'>
              {Math.abs(correlation) >= 0.7 ? 'Strong' : Math.abs(correlation) >= 0.4 ? 'Moderate' : 'Weak'}{' '}
              {correlation >= 0 ? 'positive' : 'negative'} · {segLabel} · {visibleScatterData.length} of{' '}
              {scatterData.length} shown
            </Typography>
          </Box>
        )}
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size='small'
            variant={showScatterLabels ? 'contained' : 'outlined'}
            onClick={() => setShowScatterLabels((v) => !v)}
            sx={{ textTransform: 'none' }}
          >
            {showScatterLabels ? 'Hide labels' : 'Show labels'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', width: '100%' }}>
          {/* Plot + slider — fills available width; legend sits in a fixed column */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ResponsiveContainer width='100%' height={height - 100}>
              <RechartsScatterChart margin={{ top: 16, right: 24, bottom: 48, left: 48 }}>
                <RechartsCartesianGrid strokeDasharray='3 3' stroke={theme.palette.divider} />
                <RechartsXAxis
                  type='number'
                  dataKey='x'
                  name={metricLabel(metricA)}
                  domain={chartXDomain ?? ['auto', 'auto']}
                  allowDataOverflow
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  label={{
                    value: `${metricLabel(metricA)}${metricUnit(metricA) ? ` (${metricUnit(metricA)})` : ''}`,
                    position: 'insideBottom',
                    offset: -12,
                    fontSize: 12,
                    fill: theme.palette.text.secondary,
                  }}
                />
                <RechartsYAxis
                  type='number'
                  dataKey='y'
                  name={metricLabel(metricB)}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  label={{
                    value: `${metricLabel(metricB)}${metricUnit(metricB) ? ` (${metricUnit(metricB)})` : ''}`,
                    angle: -90,
                    position: 'insideLeft',
                    offset: 12,
                    fontSize: 12,
                    fill: theme.palette.text.secondary,
                  }}
                />
                <RechartsTooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ payload }) => {
                    if (!payload?.length) return null;
                    const pt = payload[0]?.payload as (typeof scatterData)[number];
                    return (
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          fontSize: 12,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: pt.color, flexShrink: 0 }} />
                          <Typography variant='caption' fontWeight={700}>
                            {pt.label}
                          </Typography>
                        </Box>
                        <Box>
                          {metricLabel(metricA)}:{' '}
                          <strong>
                            {pt.x.toFixed(1)}
                            {metricUnit(metricA) ? ` ${metricUnit(metricA)}` : ''}
                          </strong>
                        </Box>
                        <Box>
                          {metricLabel(metricB)}:{' '}
                          <strong>
                            {pt.y.toFixed(1)}
                            {metricUnit(metricB) ? ` ${metricUnit(metricB)}` : ''}
                          </strong>
                        </Box>
                      </Box>
                    );
                  }}
                />
                <RechartsScatter data={visibleScatterData} fillOpacity={0.9}>
                  {visibleScatterData.map((p) => (
                    <RechartsCell key={p.id} fill={p.color} />
                  ))}
                  {showScatterLabels && (
                    <RechartsLabelList
                      dataKey='label'
                      position='right'
                      offset={8}
                      style={{ fontSize: 10, fill: theme.palette.text.secondary }}
                    />
                  )}
                </RechartsScatter>
                {regressionPoints && (
                  <RechartsReferenceLine
                    segment={[
                      { x: regressionPoints[0].x, y: regressionPoints[0].ry },
                      { x: regressionPoints[1].x, y: regressionPoints[1].ry },
                    ]}
                    stroke={theme.palette.warning.main}
                    strokeDasharray='5 3'
                    strokeWidth={1.5}
                    label={{
                      value: 'Trend',
                      position: 'insideTopRight',
                      fontSize: 10,
                      fill: theme.palette.warning.main,
                    }}
                  />
                )}
              </RechartsScatterChart>
            </ResponsiveContainer>
            {/* x-range slider (filters by Metric A) */}
            {xDomain && (
              <Box sx={{ px: 6, pt: 0.5 }}>
                <Slider
                  size='small'
                  value={effectiveXRange ?? xDomain}
                  min={xDomain[0]}
                  max={xDomain[1]}
                  step={sliderStep}
                  onChange={(_e, val) => Array.isArray(val) && setXRange([val[0], val[1]])}
                  valueLabelDisplay='auto'
                  valueLabelFormat={(v) => `${v}${metricUnit(metricA) ? ` ${metricUnit(metricA)}` : ''}`}
                  getAriaLabel={() => `${metricLabel(metricA)} range`}
                  sx={{ color: theme.palette.info.main }}
                />
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textAlign: 'center' }}>
                  {metricLabel(metricA)}
                </Typography>
              </Box>
            )}
          </Box>
          {/* Segment legend */}
          <Box
            sx={{
              width: 160,
              flexShrink: 0,
              maxHeight: height - 80,
              overflowY: 'auto',
              pl: 1,
              borderLeft: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant='caption' color='text.secondary' fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
              {segLabel}
            </Typography>
            <Stack spacing={0.5}>
              {scatterData.map((p) => {
                const dimmed = !visibleScatterData.some((v) => v.id === p.id);
                return (
                  <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: dimmed ? 0.35 : 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
                    <Typography variant='caption' noWrap title={p.label}>
                      {p.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSegmentedView = () => {
    if (comparisonLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (comparisonError) {
      return <Alert severity='error'>Failed to load comparison data. Please try again.</Alert>;
    }
    if (barChartData.length === 0) {
      return <Alert severity='info'>No comparison data available for the selected filters.</Alert>;
    }

    const segLabel = availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;

    return (
      <Box>
        {hiddenSegmentCount > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {hiddenSegmentCount} segment{hiddenSegmentCount !== 1 ? 's were' : ' was'} hidden due to insufficient data.
          </Alert>
        )}
        <VerticalBarChart
          data={barChartData}
          xAxisKey={segLabel}
          series={[
            {
              dataKey: metricLabel(metricA),
              label: metricLabel(metricA),
              color: theme.palette.info.main,
            },
            {
              dataKey: metricLabel(metricB),
              label: metricLabel(metricB),
              color: theme.palette.success.main,
            },
          ]}
          height={420}
          yAxisMin={0}
        />
      </Box>
    );
  };

  // Click a matrix cell → load that pair into the scatter and jump to its tab.
  const handleMatrixCellClick = (rowMetric: string, colMetric: string) => {
    if (rowMetric === colMetric) return;
    setMetricA(colMetric);
    setMetricB(rowMetric);
    setXRange(null);
    setActiveView('correlation');
  };

  // Diverging colour for a Pearson r in [-1, 1]: green positive, red negative.
  const correlationColor = (r: number | null): string => {
    if (r == null) return theme.palette.action.hover;
    const mag = Math.min(1, Math.abs(r));
    const base = r >= 0 ? theme.palette.success.main : theme.palette.error.main;
    return alpha(base, 0.12 + mag * 0.78);
  };

  const renderMatrixView = () => {
    if (matrixLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (matrixError) {
      return <Alert severity='error'>Failed to load matrix data. Please try again.</Alert>;
    }
    if (matrixMetrics.length < 2) {
      return <Alert severity='info'>Select at least 2 metrics to build a correlation matrix.</Alert>;
    }
    if (matrix.groupCount < 2) {
      return (
        <Alert severity='info'>
          Not enough segments to correlate. Try expanding the date range or changing the segment dimension.
        </Alert>
      );
    }

    const segLabel = availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    const n = matrixMetrics.length;
    const cellSize = Math.max(64, Math.min(120, Math.round(720 / (n + 1))));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* What the matrix shows */}
        <Box sx={{ maxWidth: 760, mb: 2.5, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.75 }}>
            Each square shows how strongly two metrics move together across your{' '}
            <strong>
              {matrix.groupCount} {segLabel.toLowerCase()}
            </strong>{' '}
            segments. The value is the <strong>Pearson correlation coefficient (r)</strong>, ranging from −1 to +1.
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            <strong>+1</strong> = move perfectly together · <strong>0</strong> = no linear relationship ·{' '}
            <strong>−1</strong> = move in opposite directions. <em>n</em> = segments with data for both metrics.
            Correlation is not causation. Click any cell to open that pair in the correlation scatter.
          </Typography>
        </Box>
        <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `minmax(120px, auto) repeat(${n}, ${cellSize}px)`,
              gap: '2px',
              width: 'fit-content',
              mx: 'auto',
            }}
          >
            {/* Header row: empty corner + column metric labels */}
            <Box />
            {matrixMetrics.map((m) => (
              <Box
                key={`col-${m}`}
                sx={{
                  height: 56,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 0.5,
                }}
              >
                <Typography variant='caption' fontWeight={600} sx={{ lineHeight: 1.15 }}>
                  {metricLabel(m)}
                </Typography>
              </Box>
            ))}

            {/* Body rows */}
            {matrixMetrics.map((rowM, i) => (
              <React.Fragment key={`row-${rowM}`}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    pr: 1,
                    minWidth: 120,
                  }}
                >
                  <Typography variant='caption' fontWeight={600} sx={{ textAlign: 'right', lineHeight: 1.15 }}>
                    {metricLabel(rowM)}
                  </Typography>
                </Box>
                {matrixMetrics.map((colM, j) => {
                  const { r, n: pairN } = matrix.cells[i][j];
                  const isDiag = i === j;
                  const tip = isDiag
                    ? metricLabel(rowM)
                    : r == null
                      ? `${metricLabel(rowM)} ↔ ${metricLabel(colM)}: insufficient data`
                      : `${metricLabel(rowM)} ↔ ${metricLabel(colM)}: r = ${r.toFixed(3)} (${pairN} segments)`;
                  return (
                    <Tooltip key={`${rowM}-${colM}`} title={tip} arrow disableInteractive>
                      <Box
                        onClick={() => !isDiag && handleMatrixCellClick(rowM, colM)}
                        sx={{
                          height: cellSize,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isDiag ? theme.palette.action.selected : correlationColor(r),
                          borderRadius: 1,
                          cursor: isDiag ? 'default' : 'pointer',
                          transition: 'transform 0.1s, box-shadow 0.1s',
                          '&:hover': isDiag
                            ? {}
                            : {
                                transform: 'scale(1.04)',
                                boxShadow: 2,
                              },
                        }}
                      >
                        <Typography variant='body2' fontWeight={700}>
                          {isDiag ? '—' : r == null ? '·' : r.toFixed(2)}
                        </Typography>
                        {!isDiag && r != null && (
                          <Typography variant='caption' sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                            n={pairN}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, flexWrap: 'wrap' }}>
          <Typography variant='caption' color='text.secondary'>
            Negative
          </Typography>
          <Box
            sx={{
              width: 160,
              height: 12,
              borderRadius: 1,
              background: `linear-gradient(to right, ${alpha(theme.palette.error.main, 0.9)}, ${theme.palette.action.hover}, ${alpha(theme.palette.success.main, 0.9)})`,
            }}
          />
          <Typography variant='caption' color='text.secondary'>
            Positive
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
            (−1 → +1)
          </Typography>
        </Box>
      </Box>
    );
  };

  // Square-ish scatter: tall as the card is wide (capped), so it reads like the design.
  const renderChart = () => {
    if (activeView === 'trend') return renderTrendView(560);
    if (activeView === 'correlation') return renderScatterView(720);
    if (activeView === 'matrix') return renderMatrixView();
    return renderSegmentedView();
  };

  const chartTitle = () => {
    const segLabel = availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    if (activeView === 'segmented') return `Segmented Comparison — ${segLabel}`;
    if (activeView === 'matrix') return `Correlation Matrix — ${segLabel}`;
    if (activeView === 'correlation') return `Correlation — ${metricLabel(metricA)} vs ${metricLabel(metricB)}`;
    return `Trend — ${metricLabel(metricA)} vs ${metricLabel(metricB)}`;
  };

  const chartSubtitle = () => {
    const segLabel = availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    if (activeView === 'segmented') return `Side-by-side averages grouped by ${segLabel}`;
    if (activeView === 'matrix') return `Pairwise correlations across ${matrixMetrics.length} metrics`;
    if (activeView === 'correlation')
      return `${segLabel} correlation scatter — drag the slider to filter by ${metricLabel(metricA)}`;
    return `${metricLabel(metricA)} vs ${metricLabel(metricB)} over time`;
  };

  const viewToggle = (
    <ToggleButtonGroup value={activeView} exclusive onChange={handleViewChange} size='small'>
      <ToggleButton value='trend' aria-label='Trend view'>
        <ShowChartIcon fontSize='small' sx={{ mr: 0.5 }} />
        Trend
      </ToggleButton>
      <ToggleButton value='correlation' aria-label='Correlation view'>
        <ScatterPlotIcon fontSize='small' sx={{ mr: 0.5 }} />
        Correlation
      </ToggleButton>
      <ToggleButton value='segmented' aria-label='Segmented view'>
        <BarChartIcon fontSize='small' sx={{ mr: 0.5 }} />
        Segmented
      </ToggleButton>
      <ToggleButton value='matrix' aria-label='Correlation matrix view'>
        <GridOnIcon fontSize='small' sx={{ mr: 0.5 }} />
        Matrix
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />

      <Stack spacing={3}>
        {/* ── Toolbar ────────────────────────────────────────────────────── */}
        <DashboardCard>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              gap: 2,
            }}
          >
            {/* Metric selectors + segment */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flex: 1,
                flexWrap: 'wrap',
              }}
            >
              {activeView === 'matrix' ? (
                <Autocomplete
                  multiple
                  options={METRIC_OPTIONS}
                  value={METRIC_OPTIONS.filter((m) => matrixMetrics.includes(m.value))}
                  getOptionLabel={(opt) => opt.label}
                  groupBy={(opt) => opt.group}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  disableCloseOnSelect
                  onChange={(_e, newVal) => {
                    // Preserve selection order; cap at 8 to keep the grid readable.
                    setMatrixMetrics(newVal.slice(0, 8).map((m) => m.value));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Metrics (2–8)'
                      size='small'
                      placeholder={matrixMetrics.length === 0 ? 'Select metrics' : undefined}
                    />
                  )}
                  sx={{ minWidth: 360, flex: 1 }}
                />
              ) : (
                <>
                  <Autocomplete
                    options={METRIC_OPTIONS.filter((m) => m.value !== metricB)}
                    value={METRIC_OPTIONS.find((m) => m.value === metricA) ?? null}
                    getOptionLabel={(opt) => opt.label}
                    groupBy={(opt) => opt.group}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    onChange={(_e, newVal) => {
                      if (newVal && newVal.value !== metricB) setMetricA(newVal.value);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label='Metric A' size='small' sx={{ minWidth: 220 }} />
                    )}
                    sx={{ minWidth: 220 }}
                  />
                  <Autocomplete
                    options={METRIC_OPTIONS.filter((m) => m.value !== metricA)}
                    value={METRIC_OPTIONS.find((m) => m.value === metricB) ?? null}
                    getOptionLabel={(opt) => opt.label}
                    groupBy={(opt) => opt.group}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    onChange={(_e, newVal) => {
                      if (newVal) setMetricB(newVal.value);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label='Metric B' size='small' sx={{ minWidth: 220 }} />
                    )}
                    sx={{ minWidth: 220 }}
                  />
                </>
              )}
              <Autocomplete
                options={availableSegmentOptions}
                value={availableSegmentOptions.find((s) => s.value === segmentBy) ?? availableSegmentOptions[0] ?? null}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                onChange={(_e, newVal) => {
                  if (newVal) handleSegmentByChange(newVal.value);
                }}
                renderInput={(params) => <TextField {...params} label='Segment by' size='small' />}
                sx={{ minWidth: 180 }}
              />
              {availableSegmentValues.length > 0 && (
                <Autocomplete
                  multiple
                  options={availableSegmentValues}
                  value={selectedSegmentValues}
                  onChange={(_e, newVal) => setSelectedSegmentValues(newVal)}
                  disableCloseOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Filter ${availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? 'segments'}`}
                      size='small'
                      placeholder={selectedSegmentValues.length === 0 ? 'All' : undefined}
                    />
                  )}
                  sx={{ minWidth: 240 }}
                />
              )}
            </Box>

            {/* Date + filter controls */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
              <DurationSelector
                value={dateRange}
                onChange={(range: DateRange) => setDateRange(range)}
                buttonVariant='outlined'
              />
              <Button
                variant='text'
                size='medium'
                startIcon={<FilterListIcon />}
                color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
                onClick={() => setIsFilterOpen(true)}
              >
                {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
              </Button>
            </Box>
          </Box>
        </DashboardCard>

        {/* ── Summary stat cards ─────────────────────────────────────────── */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {statsLoading ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, height: 96 }} />
            ) : (
              <SummaryStatCard
                label={`${metricLabel(metricA)} — Current Average`}
                value={statA?.value}
                unit={metricUnit(metricA)}
                change={statA?.change}
                color={theme.palette.info.main}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {statsLoading ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, height: 96 }} />
            ) : (
              <SummaryStatCard
                label={`${metricLabel(metricB)} — Current Average`}
                value={statB?.value}
                unit={metricUnit(metricB)}
                change={statB?.change}
                color={theme.palette.success.main}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Correlation coefficient
              </Typography>
              {comparisonLoading ? (
                <CircularProgress size={18} />
              ) : correlation !== null ? (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant='h5' fontWeight={700}>
                    {correlation.toFixed(3)}
                  </Typography>
                  <Chip
                    label={Math.abs(correlation) >= 0.7 ? 'Strong' : Math.abs(correlation) >= 0.4 ? 'Moderate' : 'Weak'}
                    size='small'
                    color={
                      Math.abs(correlation) >= 0.7 ? 'success' : Math.abs(correlation) >= 0.4 ? 'warning' : 'default'
                    }
                  />
                </Box>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Not enough data
                </Typography>
              )}
              <Typography variant='caption' color='text.secondary'>
                {metricLabel(metricA)} ↔ {metricLabel(metricB)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Active segment dimension
              </Typography>
              <Typography variant='h6' fontWeight={700}>
                {availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {comparisonData?.data?.groups?.length ?? '—'} groups in current period
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* ── Main chart area ────────────────────────────────────────────── */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            {(activeView === 'trend' ? trendsLoading : activeView === 'matrix' ? matrixLoading : comparisonLoading) ? (
              <DashboardCardSkeleton title subtitle height={560} />
            ) : (
              <DashboardCard title={chartTitle()} subtitle={chartSubtitle()} titleComponent={viewToggle}>
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={cohortQuery.isLoading}
                >
                  {renderChart()}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
